import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useReducer } from 'react';
import FeatureFlag from 'components/shared/FeatureFlag';
import request from 'utils/request_ajax';
import { isColorValid } from '../utils';
import SettingsCustomLinkColor from './SettingsCustomLinkColor';
import SettingsFontFamily from './SettingsFontFamily';
import SettingsLearnHeader from './SettingsLearnHeader';
import SettingsPrimaryColor from './SettingsPrimaryColor';
import SettingsSecondaryColor from './SettingsSecondaryColor';
import SettingsTextOnPrimaryColor from './SettingsTextOnPrimaryColor';
import SettingsTextOnSecondaryColor from './SettingsTextOnSecondaryColor';

const UPDATE_COLOR = 'UPDATE_COLOR';
const CLEAR_SUCCESS = 'CLEAR_SUCCESS';

const updateColor = (
  name,
  value,
  { error, success, updateSaved = false } = {},
) => ({
  type: UPDATE_COLOR,
  colorType: name,
  color: value,
  error,
  success,
  updateSaved,
});

const updateSuccesses = (state, key, success) =>
  typeof success !== 'undefined'
    ? { ...state.successes, [key]: success }
    : state.successes;

const updateErrors = (state, key, error) =>
  typeof error !== 'undefined'
    ? { ...state.errors, [key]: error }
    : state.errors;

function customizationReducer(state, action) {
  switch (action.type) {
    case CLEAR_SUCCESS: {
      const newSuccesses = { ...state.successes };
      delete newSuccesses[action.key];

      return {
        ...state,
        successes: newSuccesses,
      };
    }

    case UPDATE_COLOR: {
      const saved = action.updateSaved
        ? { ...state.saved, [action.colorType]: action.color }
        : state.saved;

      return {
        ...state,
        saved,
        [action.colorType]: action.color,
        successes: updateSuccesses(state, action.colorType, action.success),
        errors: updateErrors(state, action.colorType, action.error),
      };
    }

    default:
      return state;
  }
}

const SettingsCustomization = ({
  activeCustomLinkColor,
  activeFontFamily,
  activeLearnHeaderStyle,
  activePrimaryColor,
  activeSecondaryColor,
  activeTextOnPrimaryColor,
  activeTextOnSecondaryColor,
  defaultPrimaryColor = '',
  defaultSecondaryColor = '',
  defaultCustomLinkColor = '',
  fontFamilies,
  url,
}) => {
  const [state, dispatch] = useReducer(customizationReducer, {
    saved: {
      customLinkColor: activeCustomLinkColor,
      primaryColor: activePrimaryColor,
      secondaryColor: activeSecondaryColor,
      textOnPrimaryColor: activeTextOnPrimaryColor,
      textOnSecondaryColor: activeTextOnSecondaryColor,
    },
    customLinkColor: activeCustomLinkColor,
    primaryColor: activePrimaryColor,
    secondaryColor: activeSecondaryColor,
    textOnPrimaryColor: activeTextOnPrimaryColor,
    textOnSecondaryColor: activeTextOnSecondaryColor,
    successes: {},
    errors: {},
  });

  const clearSuccess = useCallback(key => {
    dispatch({ type: CLEAR_SUCCESS, key });
  }, []);

  const saveColor = useCallback(
    (colorType, newColor, updates, savedColor) => {
      // if error color prevent request
      // We are currently checking constrast on customLinkColor, but we should
      // eventually check them all. We need to be careful adjusting that though
      const checkContrast = colorType === 'customLinkColor';
      if (!isColorValid(newColor, checkContrast)) {
        return;
      }

      request
        .patchJSON(url, { company: updates })
        .then(() => {
          dispatch(
            updateColor(colorType, newColor, {
              success: true,
              updateSaved: true,
            }),
          );
        })
        .catch((response, statusText, err) => {
          console.error(err, response);
          dispatch(updateColor(colorType, savedColor, { error: true }));
        });
    },
    [url],
  );

  const debouncedSaveColor = useMemo(
    () => debounce(saveColor, 500),
    [saveColor],
  );

  const createOnColorChange = useCallback(
    savedColor => (name, value, updates) => {
      dispatch(updateColor(name, value, { error: false })); // optimistic update
      debouncedSaveColor(name, value, updates, savedColor);
    },
    [debouncedSaveColor],
  );

  const createOnColorOnChange = useCallback(
    savedColor => (name, value, updates) => {
      dispatch(updateColor(name, value, { error: false }));

      if (value === savedColor) {
        return;
      }

      request
        .patchJSON(url, { company: updates })
        .then(() => {
          dispatch(
            updateColor(name, value, { success: true, updateSaved: true }),
          );
        })
        .catch((response, statusText, err) => {
          console.error(err, response);
          dispatch(
            updateColor(name, savedColor, { success: false, error: true }),
          );
        });
    },
    [url],
  );

  return (
    <>
      <FeatureFlag features="custom_branding">
        <SettingsPrimaryColor
          name="primaryColor"
          color={state.primaryColor}
          savedColor={state.saved.primaryColor}
          defaultColor={defaultPrimaryColor}
          error={state.errors.primaryColor}
          success={state.successes.primaryColor}
          onChange={createOnColorChange(state.saved.primaryColor)}
          clearSuccess={clearSuccess}
        />
        <SettingsTextOnPrimaryColor
          name="textOnPrimaryColor"
          color={state.textOnPrimaryColor}
          primaryColor={state.primaryColor}
          error={state.errors.textOnPrimaryColor}
          success={state.successes.textOnPrimaryColor}
          onChange={createOnColorOnChange(state.saved.textOnPrimaryColor)}
          clearSuccess={clearSuccess}
        />
        <SettingsSecondaryColor
          name="secondaryColor"
          color={state.secondaryColor}
          savedColor={state.saved.secondaryColor}
          defaultColor={defaultSecondaryColor}
          error={state.errors.secondaryColor}
          success={state.successes.secondaryColor}
          onChange={createOnColorChange(state.saved.secondaryColor)}
          clearSuccess={clearSuccess}
        />
        <SettingsTextOnSecondaryColor
          name="textOnSecondaryColor"
          color={state.textOnSecondaryColor}
          secondaryColor={state.secondaryColor}
          error={state.errors.textOnSecondaryColor}
          success={state.successes.textOnSecondaryColor}
          onChange={createOnColorOnChange(state.saved.textOnSecondaryColor)}
          clearSuccess={clearSuccess}
        />
        <SettingsCustomLinkColor
          name="customLinkColor"
          color={state.customLinkColor}
          defaultColor={defaultCustomLinkColor}
          error={state.errors.customLinkColor}
          success={state.successes.customLinkColor}
          onChange={createOnColorChange(state.saved.customLinkColor)}
          clearSuccess={clearSuccess}
        />
      </FeatureFlag>
      <SettingsFontFamily
        url={url}
        activeFontFamily={activeFontFamily}
        fontFamilies={fontFamilies}
      />
      <SettingsLearnHeader
        url={url}
        activeLearnHeaderStyle={activeLearnHeaderStyle}
      />
    </>
  );
};

SettingsCustomization.propTypes = {};

export default props => <SettingsCustomization {...props} />;
